import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Box, Button, TextField, Typography } from '@material-ui/core';

import { ImageData } from '../redux/cardAttributeModelIndex';
import useImageData from '../hooks/useImageData';
import useWindowSize from '@rehooks/window-size';

type NumberFieldProps = {
  imgWidth: number;
}
const NumberField = styled(TextField)<NumberFieldProps>`
  width: ${props => props.imgWidth / 4 + 'px'};
`
const BaseImg = styled.img`
  position: absolute;
  user-select: none;
  filter: brightness(50%);
`
type TrimmedImgProps = {
  x: number;
  y: number;
}
const TrimmedImg = styled.img<TrimmedImgProps>`
  position: absolute;
  user-select: none;
  left: ${props => props.x + 'px'};
  top: ${props => props.y + 'px'};
`
type ResizeBoxDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  dragging: boolean;
}
const ResizeBoxDiv = styled.div<ResizeBoxDivProps>`
  position: absolute;
  z-index: 1;
  transform-origin: center;
  transform: ${props => 'translate(' + (props.x - 1) + 'px, ' + (props.y - 1) + 'px) !important'};
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border: 2px #0d47a166 solid;
  cursor: ${props => props.dragging ? undefined : 'grab'};
  overflow: hidden;
`
type ResizeBoxSvgProps = {
  x: number;
  y: number;
}
const ResizeBoxSvg = styled.svg<ResizeBoxSvgProps>`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  overflow: visible;
  transform-origin: center;
  transform: ${props => {
    return 'translate(' + props.x + 'px, ' + props.y + 'px)';
  }};
`
type ResizeHandleProps = {
  translateValue: {x: number, y: number};
}
const ResizeHandleRect = styled.rect<ResizeHandleProps>`
  position: absolute;
  transform: ${props => 'translate(' + props.translateValue.x + 'px, ' + props.translateValue.y + 'px) !important'};
  pointer-events: painted;
`
const ResizeHandleG = styled.g`
  position: absolute;
  transform: translate(0, 0);
`

type TrimDataType = { xFrom: number; xTo: number; yFrom: number; yTo: number }

type ResizeBoxProps = {
  trimData: TrimDataType;
  setTrimData: (data: TrimDataType) => void;
  scaleValue: number;
  imgWidth: number;
  imgHeight: number;
  dataUrl: string;
}
const ResizeBox: React.FC<ResizeBoxProps> = ({trimData, setTrimData, scaleValue, imgWidth, imgHeight, dataUrl}) => {
  const x = trimData.xFrom;
  const y = trimData.yFrom;
  const width = trimData.xTo - x;
  const height = trimData.yTo - y;
  const cornerWidth = width > height ? height / 4 * scaleValue : width / 4 * scaleValue;
  type SpotType = 'top' | 'rightTop' | 'right' | 'rightBottom' | 'bottom' | 'leftBottom' | 'left' | 'leftTop'
  const [dragging, setDragging] = useState<boolean>(false);

  const dragStart = (cursor: string) => {
    document.body.style.cursor = cursor;
    setDragging(true);
  }
  const dragEnd = () => {
    document.body.style.cursor = 'auto';
    setDragging(false);
  }
  const sin = (rotation: number) => {
    return rotation % 180 === 0 ? 0 : Math.sin(Math.PI * rotation / 180);
  }
  const cos = (rotation: number) => {
    return (rotation - 90) % 180 === 0 ? 0 : Math.cos(Math.PI * rotation / 180);
  }
  const getDragVerticalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (y === 0 && x > 0) return 90;
    else if (y === 0 && x < 0) return 270;
    else if (x >= 0 && y > 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x >= 0 && y < 0) return 180 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x < 0 && y < 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
  }
  const getDragHorizontalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (x === 0 && y > 0) return 90;
    else if (x === 0 && y < 0) return 270;
    else if (y >= 0 && x > 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y >= 0 && x < 0) return 180 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y < 0 && x < 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
  }
  const getHandlePosition = (spot: SpotType): {x: number, y: number} => {
    let handleXPosition = 0;
    let handleYPosition = 0;
    if (spot === 'top') {
      handleXPosition = 0;
      handleYPosition = -height / 2;
    } else if (spot === 'rightTop') {
      handleXPosition = width / 2;
      handleYPosition = -height / 2;
    } else if (spot === 'right') {
      handleXPosition = width / 2;
      handleYPosition = 0;
    } else if (spot === 'rightBottom') {
      handleXPosition = width / 2;
      handleYPosition = height / 2;
    } else if (spot === 'bottom') {
      handleXPosition = 0;
      handleYPosition = height / 2;
    } else if (spot === 'leftBottom') {
      handleXPosition = -width / 2;
      handleYPosition = height / 2;
    } else if (spot === 'left') {
      handleXPosition = -width / 2;
      handleYPosition = 0;
    } else if (spot === 'leftTop') {
      handleXPosition = -width / 2;
      handleYPosition = -height / 2;
    }
    const handleX = x + width / 2 + handleXPosition;
    const handleY = y + height / 2 + handleYPosition;
    return { x: handleX, y: handleY };
  }
  const resizeItemTop = (data: DraggableData) => {
    const origin = getHandlePosition('top');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragVerticalAngle(-drag.x, drag.y);
    const dragValue = cos(dragAngle) === 0 ? Math.abs(drag.x / sin(dragAngle)) : Math.abs(drag.y / cos(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const xFrom = x;
    const resizedYFrom = y + resize * scaleValue;
    const yFrom = resizedYFrom < 0 ? 0 : resizedYFrom > trimData.yTo - 1 ? trimData.yTo - 1 : resizedYFrom;
    const xTo = trimData.xTo;
    const yTo = trimData.yTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemRightTop = (data: DraggableData) => {
    const origin = getHandlePosition('rightTop');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const xFrom = x;
    const resizedYFrom = y + resize.height * scaleValue;
    const yFrom = resizedYFrom < 0 ? 0 : resizedYFrom > trimData.yTo - 1 ? trimData.yTo - 1 : resizedYFrom;
    const resizedXTo = trimData.xTo + resize.width * scaleValue;
    const xTo = resizedXTo < x + 1 ? x + 1 : resizedXTo > imgWidth ? imgWidth : resizedXTo;
    const yTo = trimData.yTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemRight = (data: DraggableData) => {
    const origin = getHandlePosition('right');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragHorizontalAngle(drag.x, drag.y);
    const dragValue = sin(dragAngle) === 0 ? Math.abs(drag.x / cos(dragAngle)) : Math.abs(drag.y / sin(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const xFrom = x;
    const yFrom = y;
    const resizedXTo = trimData.xTo + resize * scaleValue;
    const xTo = resizedXTo < x + 1 ? x + 1 : resizedXTo > imgWidth ? imgWidth : resizedXTo;
    const yTo = trimData.yTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemRightBottom = (data: DraggableData) => {
    const origin = getHandlePosition('rightBottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const xFrom = x;
    const yFrom = y;
    const resizedXTo = trimData.xTo + resize.width * scaleValue;
    const xTo = resizedXTo < x + 1 ? x + 1 : resizedXTo > imgWidth ? imgWidth : resizedXTo;
    const resizedYTo = trimData.yTo + resize.height * scaleValue;
    const yTo = resizedYTo < y + 1 ? y + 1 : resizedYTo > imgHeight ? imgHeight : resizedYTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemBottom = (data: DraggableData) => {
    const origin = getHandlePosition('bottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragVerticalAngle(-drag.x, drag.y);
    const dragValue = cos(dragAngle) === 0 ? Math.abs(drag.x / sin(dragAngle)) : Math.abs(drag.y / cos(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const xFrom = x;
    const yFrom = y;
    const xTo = trimData.xTo;
    const resizedYTo = trimData.yTo + resize * scaleValue;
    const yTo = resizedYTo < y + 1 ? y + 1 : resizedYTo > imgHeight ? imgHeight : resizedYTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemLeftBottom = (data: DraggableData) => {
    const origin = getHandlePosition('leftBottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedXFrom = x + resize.width * scaleValue;
    const xFrom = resizedXFrom < 0 ? 0 : resizedXFrom > trimData.xTo - 1 ? trimData.xTo - 1 : resizedXFrom;
    const yFrom = y;
    const xTo = trimData.xTo;
    const resizedYTo = trimData.yTo + resize.height * scaleValue;
    const yTo = resizedYTo < y + 1 ? y + 1 : resizedYTo > imgHeight ? imgHeight : resizedYTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemLeft = (data: DraggableData) => {
    const origin = getHandlePosition('left');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragHorizontalAngle(drag.x, drag.y);
    const dragValue = sin(dragAngle) === 0 ? Math.abs(drag.x / cos(dragAngle)) : Math.abs(drag.y / sin(dragAngle));
    const resize = dragValue * cos(-dragAngle);

    const resizedXFrom = x + resize * scaleValue;
    const xFrom = resizedXFrom < 0 ? 0 : resizedXFrom > trimData.xTo - 1 ? trimData.xTo - 1 : resizedXFrom;
    const yFrom = y;
    const xTo = trimData.xTo;
    const yTo = trimData.yTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const resizeItemLeftTop = (data: DraggableData) => {
    const origin = getHandlePosition('leftTop');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x, height: drag.y }

    const resizedXFrom = x + resize.width * scaleValue;
    const xFrom = resizedXFrom < 0 ? 0 : resizedXFrom > trimData.xTo - 1 ? trimData.xTo - 1 : resizedXFrom;
    const resizedYFrom = y + resize.height * scaleValue;
    const yFrom = resizedYFrom < 0 ? 0 : resizedYFrom > trimData.yTo - 1 ? trimData.yTo - 1 : resizedYFrom;
    const xTo = trimData.xTo;
    const yTo = trimData.yTo;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }
  const dragBox = (data: DraggableData) => {
    const xFrom = x + data.deltaX < 0 ? 0 : trimData.xTo + data.deltaX > imgWidth ? imgWidth - width : x + data.deltaX;
    const yFrom = y + data.deltaY < 0 ? 0 : trimData.yTo + data.deltaY > imgHeight ? imgHeight - height : y + data.deltaY;
    const xTo = x + data.deltaX < 0 ? width : trimData.xTo + data.deltaX > imgWidth ? imgWidth : trimData.xTo + data.deltaX;
    const yTo = y + data.deltaY < 0 ? height: trimData.yTo + data.deltaY > imgHeight ? imgHeight : trimData.yTo + data.deltaY;
    setTrimData({ xFrom, yFrom, xTo, yTo });
  }

  return (
    <>
      <Draggable position={{x: x * scaleValue, y: y * scaleValue}} scale={scaleValue}
        onStart={() => dragStart('grabbing')}
        onDrag={(ev: DraggableEvent, data: DraggableData) => dragBox(data)}
        onStop={dragEnd}
      >
        <ResizeBoxDiv width={width * scaleValue} height={height * scaleValue}
          x={x * scaleValue} y={y * scaleValue} dragging={dragging}>
          <TrimmedImg src={dataUrl} draggable="false" x={-x * scaleValue - 1} y={-y * scaleValue - 1}
            width={imgWidth * scaleValue} height={imgHeight * scaleValue} />
        </ResizeBoxDiv>
      </Draggable>
      <ResizeBoxSvg width={width * scaleValue} height={height * scaleValue} x={x * scaleValue} y={y * scaleValue}
        xmlns="http://www.w3.org/2000/svg">
        <Draggable position={getHandlePosition('top')} scale={scaleValue}
          onStart={() => dragStart('ns-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={width / 3 * scaleValue} height={4} fill="#0d47a1" stroke="#0d47a1" strokeWidth="1"
            cursor={dragging ? undefined : 'ns-resize'}
            translateValue={{x: width / 3 * scaleValue + 1, y: -2}} />
        </Draggable>
        <Draggable position={getHandlePosition('rightTop')} scale={scaleValue}
          onStart={() => dragStart('nesw-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRightTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill="#0d47a1" stroke="#0d47a1" strokeWidth="1" cursor={dragging ? undefined : 'nesw-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: width * scaleValue - cornerWidth + 4, y: -2}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: width * scaleValue, y: -2}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('right')} scale={scaleValue}
          onStart={() => dragStart('ew-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRight(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={4} height={height / 3 * scaleValue} fill="#0d47a1" stroke="#0d47a1" strokeWidth="1"
            cursor={dragging ? undefined : 'ew-resize'}
            translateValue={{x: width * scaleValue, y: height / 3 * scaleValue + 1}} />
        </Draggable>
        <Draggable position={getHandlePosition('rightBottom')} scale={scaleValue}
          onStart={() => dragStart('nwse-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRightBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill="#0d47a1" stroke="#0d47a1" strokeWidth="1" cursor={dragging ? undefined : 'nwse-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: width * scaleValue - cornerWidth + 4, y: height * scaleValue}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: width * scaleValue, y: height * scaleValue - cornerWidth + 4}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('bottom')} scale={scaleValue}
          onStart={() => dragStart('ns-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={width / 3 * scaleValue} height={4} fill="#0d47a1" stroke="#0d47a1" strokeWidth="1"
            cursor={dragging ? undefined : 'ns-resize'}
            translateValue={{x: width / 3 * scaleValue + 1, y: height * scaleValue}} />
        </Draggable>
        <Draggable position={getHandlePosition('leftBottom')} scale={scaleValue}
          onStart={() => dragStart('nesw-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeftBottom(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill="#0d47a1" stroke="#0d47a1" strokeWidth="1" cursor={dragging ? undefined : 'nesw-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: -2, y: height * scaleValue}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: -2, y: height * scaleValue - cornerWidth + 4}} />
          </ResizeHandleG>
        </Draggable>
        <Draggable position={getHandlePosition('left')} scale={scaleValue}
          onStart={() => dragStart('ew-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeft(data)}
          onStop={dragEnd}
          >
          <ResizeHandleRect width={4} height={height / 3 * scaleValue} fill="#0d47a1" stroke="#0d47a1" strokeWidth="1"
            cursor={dragging ? undefined : 'ew-resize'}
            translateValue={{x: -2, y: height / 3 * scaleValue + 1}} />
        </Draggable>
        <Draggable position={getHandlePosition('leftTop')} scale={scaleValue}
          onStart={() => dragStart('nwse-resize')}
          onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemLeftTop(data)}
          onStop={dragEnd}
          >
          <ResizeHandleG fill="#0d47a1" stroke="#0d47a1" strokeWidth="1" cursor={dragging ? undefined : 'nwse-resize'}>
            <ResizeHandleRect width={cornerWidth} height={4}
              translateValue={{x: -2, y: -2}} />
            <ResizeHandleRect width={4} height={cornerWidth}
              translateValue={{x: -2, y: -2}} />
          </ResizeHandleG>
        </Draggable>
      </ResizeBoxSvg>
    </>
  );
}

type TrimmingDialogProps = {
  open: boolean;
  onClose: () => void;
  data: ImageData;
  changeData: (data: ImageData) => void;
}
const TrimmingDialog: React.FC<TrimmingDialogProps> = ({open, onClose, data, changeData}) => {
  const dataUrl = useImageData(data.imageId);
  const [trimData, setTrimData] = useState<TrimDataType>({ xFrom: data.trimXFrom, xTo: data.trimXTo, yFrom: data.trimYFrom, yTo: data.trimYTo });

  useEffect(() => setTrimData({ xFrom: data.trimXFrom, xTo: data.trimXTo, yFrom: data.trimYFrom, yTo: data.trimYTo }), [open])

  const windowSize = useWindowSize();
  const workWidth = windowSize.innerWidth - (32 + 24 + 32) * 2;
  const workHeight = windowSize.innerHeight - 64 * 2 - (32 + 8) * 2 - 61 - 24 - 16;
  const img = new Image();
  img.src = dataUrl;
  img.style.display = 'none';
  const width = img.width;
  const height = img.height;
  const ratioByHeight = workHeight / height;
  const actualWidth = width * ratioByHeight;
  const ratio = actualWidth > workWidth ? workWidth / width : ratioByHeight;
  img.remove();

  const changeDataDisplay = (kind: string, value: number) => {
    switch (kind) {
      case 'xFrom': setTrimData({ ...trimData, xFrom: value }); break;
      case 'yFrom': setTrimData({ ...trimData, yFrom: value }); break;
      case 'xTo': setTrimData({ ...trimData, xTo: value }); break;
      case 'yTo': setTrimData({ ...trimData, yTo: value }); break;
    }
  }
  const changeTrimData = (kind: string, value: number, min: number, max: number) => {
    const newValue = value < min ? min : value > max ? max : value;
    switch (kind) {
      case 'xFrom': setTrimData({ ...trimData, xFrom: newValue }); break;
      case 'yFrom': setTrimData({ ...trimData, yFrom: newValue }); break;
      case 'xTo': setTrimData({ ...trimData, xTo: newValue }); break;
      case 'yTo': setTrimData({ ...trimData, yTo: newValue }); break;
    }
  }
  const changeExe = () => {
    changeData({
      ...data,
      trimXFrom: trimData.xFrom,
      trimXTo: trimData.xTo,
      trimYFrom: trimData.yFrom,
      trimYTo: trimData.yTo
    });
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xl'>
      <DialogTitle>画像をトリミングします</DialogTitle>
      <DialogContent>
        <Box pl={2}>
          <Typography variant="caption" style={{position: 'relative', left: 0, top: -5}}>左上</Typography>
          <Typography variant="caption" style={{position: 'absolute', left: width * ratio / 2 + 40 + 12 + 32}}>右下</Typography>
        </Box>
        <Box pl={1} mb={2}>
          <NumberField variant='outlined' label='X'
            type="number" inputProps={{step: 1, min: 0, max: trimData.xTo}}
            value={trimData.xFrom}
            imgWidth={width * ratio}
            style={{marginRight: 8}}
            onChange={(ev) => changeDataDisplay('xFrom', Number(ev.currentTarget.value))}
            onBlur={(ev) => changeTrimData('xFrom', Number(ev.currentTarget.value), 0, trimData.xTo)}
          />
          <NumberField variant='outlined' label='Y'
            type="number" inputProps={{step: 1, min: 0, max: trimData.yTo}}
            value={trimData.yFrom}
            imgWidth={width * ratio}
            style={{marginRight: 32}}
            onChange={(ev) => changeDataDisplay('yFrom', Number(ev.currentTarget.value))}
            onBlur={(ev) => changeTrimData('yFrom', Number(ev.currentTarget.value), 0, trimData.yTo)}
          />
          <NumberField variant='outlined' label='X'
            type="number" inputProps={{step: 1, min: trimData.xFrom, max: width}}
            value={trimData.xTo}
            imgWidth={width * ratio}
            style={{marginRight: 8}}
            onChange={(ev) => changeDataDisplay('xTo', Number(ev.currentTarget.value))}
            onBlur={(ev) => changeTrimData('xTo', Number(ev.currentTarget.value), trimData.xFrom, width)}
          />
          <NumberField variant='outlined' label='Y'
            type="number" inputProps={{step: 1, min: trimData.yFrom, max: height}}
            value={trimData.yTo}
            imgWidth={width * ratio}
            onChange={(ev) => changeDataDisplay('yTo', Number(ev.currentTarget.value))}
            onBlur={(ev) => changeTrimData('yTo', Number(ev.currentTarget.value), trimData.yFrom, height)}
          />
        </Box>
        <div style={{marginLeft: 32, marginRight: 32, width: width * ratio, height: height * ratio}}>
          <BaseImg src={dataUrl} draggable="false"
            width={width * ratio} height={height * ratio} />
          <ResizeBox trimData={trimData} setTrimData={setTrimData}
            scaleValue={ratio}
            imgWidth={width} imgHeight={height}
            dataUrl={dataUrl} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant='text' color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button variant='text' color='primary' onClick={changeExe}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TrimmingDialog;
